/**
 * EquipmentDetailEditorStep — Step-2 gép-detail-editor paradigma (Slice-2 katalógus)
 *
 * Feature flag: `RENTAL_STEP2_DETAIL_EDITOR` (2026-06-06 flip: dev/demo default ON, prod OFF).
 * Routing: `StepWizardShell` step-2 ágában, flag-ON esetén az
 * `EquipmentPackageStepV2` helyett rendereli ezt a komponenst.
 *
 * 3-panel elrendezés (design-referencia: steps2.jsx + theme.css 2026-06-05):
 *  - Bal rail (MachinesRail): kosárgépek listája, sorszám + gépnév + kód,
 *    kattintható → aktív gép (lokál useState, slice-1). "+ Gép hozzáadása" CTA.
 *  - Közép (StepMachines shell): aktív gép fejléc + csomag-választó
 *    (`usePackagesForEquipment` + `PackageContentBreakdown` reuse) +
 *    tartozék-katalógus (`AccessoryCatalogSection`, slice-2) +
 *    jelmagyarázat (`AccessoryLegend`).
 *  - Jobb kalk (MachineCalcSide, slice-3).
 *
 * STORE-SEAM döntés (slice-2):
 *  A `wizard-store.ts`-ben a `selectedAccessories: Map<equipmentId, accessoryId[]>`
 *  (toggleAccessory + getAccessoriesForEquipment) SET-alapú — per-gép accessory
 *  PRESENCE, NEM qty. Per-qty persistencia NEM áll rendelkezésre a store-ban.
 *  Döntés: a qty-stepper LOKÁL komponens-state (`Record<accessoryId, number>`),
 *  a lineTotal is ebből számított. A qty-persistencia + cart-pricing aggregáció
 *  a Zeno BE-story #2 `calculateCartPricing`-extension seam-je — ez a slice
 *  display+local-qty scope (grandTotal-aggregáció NEM ennek a scope-ja).
 *
 * NEM scope (slice-3+):
 *  - "+ Gép hozzáadása" valódi add-flow (egyelőre placeholder toast)
 *  - Qty-persistencia a store-ban (Zeno #2 seam)
 */

import type { Package } from '@/api/packages';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePackagesForEquipment } from '@/hooks/use-packages';
import { useAccessoriesForEquipment, useBergep } from '@/hooks/use-rental-equipment';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { formatCurrency } from '../mock-data';
import type { CartItem, Equipment } from '../types';
import {
  CATEGORY_LABEL_KEYS,
  EquipmentCategory,
  EquipmentCondition,
  EquipmentStatus,
  STATUS_LABEL_KEYS,
} from '../types';
import { useWizardStore } from '../wizard-store';
import { prioritizeAvailable } from './equipment-availability';
import { PackageContentBreakdown } from './EquipmentPackageStepV2';

// ─── Jelmagyarázat (legend) — Slice-2 ──────────────────────────────────────

/**
 * AccessoryLegend — 3 soros jelmagyarázat a csomag-item settlement-kategóriákhoz.
 * Rendereli a FREE/RENTABLE/CONSIGNMENT sor-betekintőket a `SETTLEMENT_BADGE` színeivel.
 * Render-only; a tényleges badge-eket az egyes item-ek hordozzák.
 * Csak a `RENTAL_STEP2_DETAIL_EDITOR` flag-ON ágban (EquipmentDetailEditorStep) renderel.
 */
function AccessoryLegend() {
  const { t } = useTranslation();

  const items = [
    {
      key: 'free' as const,
      dot: 'bg-emerald-500',
      label: t('rental.wizard.v2.legend.free'),
      hint: t('rental.wizard.v2.detail.legendFreeHint'),
    },
    {
      key: 'rentable' as const,
      dot: 'bg-blue-500',
      label: t('rental.wizard.v2.legend.rentable'),
      hint: t('rental.wizard.v2.detail.legendRentableHint'),
    },
    {
      key: 'consignment' as const,
      dot: 'bg-amber-500',
      label: t('rental.wizard.v2.legend.consignment'),
      hint: t('rental.wizard.v2.detail.legendConsignHint'),
    },
  ] as const;

  return (
    <div
      data-testid="detail-editor-accessory-legend"
      aria-label={t('rental.wizard.v2.detail.legendTitle')}
    >
      <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('rental.wizard.v2.detail.legendTitle')}
      </span>
      <ul className="space-y-1" data-testid="detail-editor-legend-list">
        {items.map(it => (
          <li
            key={it.key}
            className="flex items-center gap-2 text-[11px]"
            data-testid={`detail-editor-legend-item-${it.key}`}
          >
            <span className={cn('h-2 w-2 shrink-0 rounded-full', it.dot)} aria-hidden="true" />
            <span className="font-medium text-foreground">{it.label}</span>
            <span className="text-muted-foreground">— {it.hint}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Tartozék-katalógus (slice-2) ───────────────────────────────────────────

/**
 * SETTLEMENT_BADGE_CATALOG — KIND → badge style leképezés a katalógus-sorokban.
 * REUSE: azonos emerald/blue/amber palettát követi mint a `SettlementBadge`
 * az `EquipmentPackageStepV2`-ben (NEM definiál új szín-mappinget).
 */
const SETTLEMENT_BADGE_CATALOG: Record<
  'FREE' | 'RENTABLE' | 'CONSIGNMENT',
  { cls: string; dot: string; labelKey: string }
> = {
  FREE: {
    dot: 'bg-emerald-500',
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    labelKey: 'rental.wizard.v2.legend.free',
  },
  RENTABLE: {
    dot: 'bg-blue-500',
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    labelKey: 'rental.wizard.v2.legend.rentable',
  },
  CONSIGNMENT: {
    dot: 'bg-amber-500',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    labelKey: 'rental.wizard.v2.legend.consignment',
  },
};

/**
 * AccessoryCatalogSection — kategória-tab sáv + tartozék-sorok.
 *
 * Adat-forrás kizárólag: `useAccessoriesForEquipment(equipmentId)`.
 * Qty-state: STORE-WIRED (BE-story #2 — `setAccessoryCatalogQty` + `getAccessoryCatalogQtys`).
 * Az ár-snapshot a kiválasztáskor kerül a store-ba (ADR-108 snapshot-konzisztens).
 *
 * days: a store `pricing.durationDays`-ből. Ha nincs pricing (időszak nincs
 * beállítva), 1-es fallback (RENTABLE lineTotal = dailyRate × qty × 1).
 */
function AccessoryCatalogSection({ equipmentId, days }: { equipmentId: string; days: number }) {
  const { t } = useTranslation();
  const { accessories, categories, isLoading, error } = useAccessoriesForEquipment(equipmentId);

  // BE-story #2: store az egyetlen igazság (SSoT) — lokál qty-mirror eltávolítva.
  // getQty REAKTÍVAN olvas a store-ból az aktuális equipmentId-re: gép-váltás /
  // remount esetén automatikusan a store-értéket mutatja (desync megszűnik).
  // ADR-108 snapshot-konzisztens: a store `calculateCartPricing`-et táplálja.
  const setAccessoryCatalogQty = useWizardStore(s => s.setAccessoryCatalogQty);
  const accessoryQtysForEq = useWizardStore(s => s.accessoryCatalogQtys.get(equipmentId));
  const [activeTab, setActiveTab] = useState<string>('all');

  const getQty = (id: string) => accessoryQtysForEq?.[id]?.qty ?? 0;
  const setQty = (
    id: string,
    n: number,
    snapshot: { kind: 'FREE' | 'RENTABLE' | 'CONSIGNMENT'; dailyRate: number; price: number | null }
  ) => {
    // Store-only frissítés (SSoT) — a selector-subscription (accessoryQtysForEq)
    // garantálja a re-rendert minden setAccessoryCatalogQty hívás után.
    setAccessoryCatalogQty(equipmentId, id, snapshot, n);
  };

  // Tab-szűrés
  const cats = ['all', ...categories];
  const catCount = (c: string) =>
    c === 'all' ? accessories.length : accessories.filter(a => a.cat === c).length;
  const shown = accessories.filter(a => activeTab === 'all' || a.cat === activeTab);

  // Loading állapot
  if (isLoading) {
    return (
      <div
        className="text-xs italic text-muted-foreground"
        data-testid="detail-editor-catalog-loading"
      >
        {t('rental.wizard.v2.detail.accCatalogLoading')}
      </div>
    );
  }

  // Error állapot
  if (error) {
    return (
      <div className="text-xs text-destructive" data-testid="detail-editor-catalog-error">
        {t('rental.wizard.v2.detail.accCatalogError')}
      </div>
    );
  }

  // Empty állapot
  if (accessories.length === 0) {
    return (
      <div
        className="text-xs italic text-muted-foreground"
        data-testid="detail-editor-catalog-empty"
      >
        {t('rental.wizard.v2.detail.accCatalogEmpty')}
      </div>
    );
  }

  return (
    <section data-testid="detail-editor-catalog-section">
      {/* Katalógus cím */}
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('rental.wizard.v2.detail.accCatalogTitle')}
      </h3>

      {/* Kategória-tab sáv */}
      <div
        className="mb-3 flex flex-wrap gap-1.5"
        data-testid="detail-editor-catalog-tabs"
        role="tablist"
      >
        {cats.map(c => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={activeTab === c}
            onClick={() => setActiveTab(c)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
              activeTab === c
                ? 'border-[var(--accent,#0f766e)] bg-[var(--accent,#0f766e)] text-white'
                : 'border-border text-muted-foreground hover:border-border-strong hover:text-fg'
            )}
            data-testid={`detail-editor-catalog-tab-${c}`}
          >
            {c === 'all' ? t('rental.wizard.v2.detail.accTabAll') : c}
            <span
              className="opacity-70 font-bold"
              data-testid={`detail-editor-catalog-tab-count-${c}`}
            >
              {catCount(c)}
            </span>
          </button>
        ))}
      </div>

      {/* Tartozék-sorok */}
      <ul className="space-y-1.5" data-testid="detail-editor-catalog-rows">
        {shown.map(a => {
          const qty = getQty(a.id);
          const badgeCfg = SETTLEMENT_BADGE_CATALOG[a.kind];
          let lineTotal = 0;
          let unitLabel: string;
          if (a.kind === 'RENTABLE') {
            lineTotal = a.dailyRate * qty * days;
            unitLabel = `${a.dailyRate} ${t('rental.wizard.v2.detail.rentableUnit')}`;
          } else if (a.kind === 'CONSIGNMENT') {
            lineTotal = (a.price ?? 0) * qty;
            unitLabel = `${a.price ?? 0} ${t('rental.wizard.v2.detail.consignUnit')}`;
          } else {
            // FREE
            lineTotal = 0;
            unitLabel = t('rental.wizard.v2.detail.accFree');
          }

          return (
            <li
              key={a.id}
              className={cn(
                'flex items-center gap-3 rounded-md border px-3 py-2 transition-colors',
                qty > 0
                  ? 'border-[var(--accent,#0f766e)] bg-[var(--accent-soft,#e6f4f1)]'
                  : 'border-border'
              )}
              data-testid={`detail-editor-catalog-row-${a.id}`}
              data-qty={qty}
            >
              {/* Avatar/ikon */}
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-bg text-[18px]"
                aria-hidden="true"
              >
                {a.kind === 'FREE' ? '✓' : a.kind === 'RENTABLE' ? '◆' : '⚠'}
              </span>

              {/* Közép: név + badge + sku + unit */}
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="text-[13px] font-semibold"
                    data-testid={`detail-editor-catalog-row-name-${a.id}`}
                  >
                    {a.name}
                  </span>
                  {/* KIND badge — REUSE emerald/blue/amber palette mint SettlementBadge */}
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                      badgeCfg.cls
                    )}
                    data-testid={`detail-editor-catalog-kind-badge-${a.id}`}
                  >
                    <span
                      className={cn('h-1.5 w-1.5 shrink-0 rounded-full', badgeCfg.dot)}
                      aria-hidden="true"
                    />
                    {t(badgeCfg.labelKey)}
                  </span>
                  <span
                    className="font-mono text-[11px] text-muted-foreground"
                    data-testid={`detail-editor-catalog-row-sku-${a.id}`}
                  >
                    {a.sku}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {unitLabel}
                  {' · '}
                  <span data-testid={`detail-editor-catalog-row-stock-${a.id}`}>
                    {t('rental.wizard.v2.detail.accStockN', { n: a.stock })}
                  </span>
                </div>
              </div>

              {/* Jobb: lineTotal + qty-stepper */}
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                {/* lineTotal */}
                <span
                  className={cn(
                    'tabular-nums text-[14px] font-bold',
                    lineTotal > 0 ? 'text-foreground' : 'text-muted-foreground'
                  )}
                  data-testid={`detail-editor-catalog-row-linetotal-${a.id}`}
                  data-linetotal={lineTotal}
                >
                  {lineTotal > 0 ? `${lineTotal} Ft` : '—'}
                </span>
                {/* qty-stepper — REUSE PackageItemStepper stílusát */}
                <div
                  className={cn('flex items-center gap-1', qty === 0 && 'opacity-60')}
                  data-testid={`detail-editor-catalog-stepper-${a.id}`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setQty(a.id, qty - 1, {
                        kind: a.kind,
                        dailyRate: a.dailyRate,
                        price: a.price,
                      })
                    }
                    className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted"
                    aria-label={t('rental.wizard.v2.itemQtyDec')}
                    data-testid={`detail-editor-catalog-dec-${a.id}`}
                  >
                    −
                  </button>
                  <span
                    className="w-6 text-center tabular-nums text-[12px]"
                    data-testid={`detail-editor-catalog-qty-${a.id}`}
                  >
                    {qty}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setQty(a.id, qty + 1, {
                        kind: a.kind,
                        dailyRate: a.dailyRate,
                        price: a.price,
                      })
                    }
                    className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted"
                    aria-label={t('rental.wizard.v2.itemQtyInc')}
                    data-testid={`detail-editor-catalog-inc-${a.id}`}
                  >
                    +
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─── Gép-mapper: BergepItem → Equipment (REUSE V2 toV2WizardEquipment mintájára) ────

/**
 * toDetailEditorEquipment — BergepItem API shape → Equipment wizard shape.
 * REUSE: azonos konverzió mint a V2 `toV2WizardEquipment` (EquipmentPackageStepV2.tsx:68).
 * TODO: `useEquipmentPicker()` shared hook extract Sprint 27+ retire-by gate
 * (lásd: V2 "Duplicate toWizardEquipment helper" komment + Amelia party-mode precedens).
 */
function toDetailEditorEquipment(item: {
  id: string;
  name: string;
  inventoryCode?: string;
  serialNumber: string;
  category: string;
  brand?: string;
  model?: string;
  status: string;
  condition: string;
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  hourlyRate?: number;
  depositAmount: number;
}): Equipment {
  const eq: Equipment = {
    id: item.id,
    name: item.name,
    equipmentCode: item.inventoryCode,
    serialNumber: item.serialNumber,
    category: (item.category as EquipmentCategory) ?? EquipmentCategory.POWER_TOOL,
    brand: item.brand,
    model: item.model,
    status: (item.status as EquipmentStatus) ?? EquipmentStatus.AVAILABLE,
    condition: (item.condition as EquipmentCondition) ?? EquipmentCondition.GOOD,
    dailyRate: item.dailyRate,
    weeklyRate: item.weeklyRate,
    monthlyRate: item.monthlyRate,
    depositAmount: item.depositAmount,
  };
  if (item.hourlyRate != null && item.hourlyRate > 0) {
    eq.hourlyRate = item.hourlyRate;
  }
  return eq;
}

// ─── AddMachineDialog ───────────────────────────────────────────────────────

/**
 * AddMachineDialog — gép-kereső dialógus a MachinesRail "+ Gép hozzáadása" CTA-jához.
 *
 * REUSE-primitívek (V2 V2EquipmentPicker mintájára, EquipmentPackageStepV2.tsx:599):
 *  - `useBergep({ availableOnly:false, search:debouncedSearch, page:1, pageSize:20 })` — server-side search
 *  - `useDebouncedValue(search.trim(), 250)` — 250ms debounce
 *  - `addToCart(equipment)` / `isInCart(equipment.id)` — wizard-store primitívek
 *  - `toDetailEditorEquipment` mapper — BergepItem → Equipment wizard shape
 *  - guard: RENTED/!AVAILABLE → disabled + STATUS_LABEL_KEYS badge (mint V2)
 *  - guard: isInCart → "már kosárban" toast + skip (mint V2)
 *
 * Viselkedés add után: dialog zár + a frissen hozzáadott gép az onAfterAdd callback-en
 * keresztül aktívvá válik a railben (a MachinesRail parent megkapja az equipment.id-t).
 *
 * TODO: `useEquipmentPicker()` shared hook extract — follow-up (V2 + ez a komponens
 * mindkettő tartalmaz duplikált pickup-logikát; dedup külön PR, nincs spec a V2-re).
 */
interface AddMachineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Callback: add után az equipment.id-vel — a parent a cart-ból keresi ki a cart-item.id-t. */
  onAfterAdd?: (equipmentId: string) => void;
}

function AddMachineDialog({ open, onOpenChange, onAfterAdd }: AddMachineDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 250);

  const { addToCart, isInCart } = useWizardStore(
    useShallow(s => ({
      addToCart: s.addToCart,
      isInCart: s.isInCart,
    }))
  );

  // REUSE V2EquipmentPicker: availableOnly:false → RENTED gépek is látszanak disabled-ként
  const { equipment: apiEquipment, isLoading } = useBergep({
    availableOnly: false,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    page: 1,
    pageSize: 20,
  });

  const results = useMemo(
    () => prioritizeAvailable(apiEquipment.map(toDetailEditorEquipment)).slice(0, 20),
    [apiEquipment]
  );

  const handleAdd = (equipment: Equipment) => {
    // REUSE V2EquipmentPicker guard-ok (EquipmentPackageStepV2.tsx:646-659)
    if (equipment.status !== EquipmentStatus.AVAILABLE) return;
    if (isInCart(equipment.id)) {
      toast({
        title: t('rental.wizard.v2.search.alreadyInCart', { name: equipment.name }),
        variant: 'default',
      });
      return;
    }
    addToCart(equipment);
    toast({
      title: t('rental.wizard.v2.search.addedToCart', { name: equipment.name }),
      variant: 'default',
    });
    // Dialog zár + az onAfterAdd callback-el jelzünk a parentnek az equipment.id-vel.
    // A parent a következő render-ciklusban a frissített cart-ból kikeresi a cart-item.id-t.
    onOpenChange(false);
    if (onAfterAdd) onAfterAdd(equipment.id);
    setSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-machine-dialog">
        <DialogHeader>
          <DialogTitle data-testid="add-machine-dialog-title">
            {t('rental.wizard.v2.detail.addMachineDialogTitle')}
          </DialogTitle>
        </DialogHeader>

        {/* Kereső-input */}
        <div className="mt-2">
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('rental.wizard.v2.detail.addMachineSearchPlaceholder')}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="add-machine-search-input"
            autoFocus
            autoComplete="off"
          />
        </div>

        {/* Találati lista */}
        <div className="mt-3 max-h-[360px] overflow-y-auto" data-testid="add-machine-results">
          {isLoading ? (
            <div
              className="py-4 text-center text-sm italic text-muted-foreground"
              data-testid="add-machine-results-loading"
            >
              {t('rental.wizard.v2.search.loading')}
            </div>
          ) : results.length === 0 ? (
            <div
              className="py-4 text-center text-sm italic text-muted-foreground"
              data-testid="add-machine-results-empty"
            >
              {debouncedSearch
                ? t('rental.wizard.v2.search.noResults', { query: debouncedSearch })
                : t('rental.wizard.v2.detail.addMachineHint')}
            </div>
          ) : (
            <ul
              className="divide-y divide-border rounded-md border border-border"
              data-testid="add-machine-results-list"
            >
              {results.map(eq => {
                const inCart = isInCart(eq.id);
                const isAvailable = eq.status === EquipmentStatus.AVAILABLE;
                const disabled = inCart || !isAvailable;
                return (
                  <li key={eq.id}>
                    <button
                      type="button"
                      onClick={() => handleAdd(eq)}
                      disabled={disabled}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-xs transition-colors hover:bg-accent-soft',
                        disabled && 'cursor-not-allowed opacity-50'
                      )}
                      data-testid={`add-machine-result-${eq.id}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate font-semibold text-[13px]"
                          data-testid={`add-machine-result-name-${eq.id}`}
                        >
                          {eq.name}
                        </span>
                        {eq.equipmentCode && (
                          <span
                            className="block font-mono text-[11px] text-muted-foreground"
                            data-testid={`add-machine-result-sku-${eq.id}`}
                          >
                            {eq.equipmentCode}
                          </span>
                        )}
                        <span className="block text-[11px] text-muted-foreground">
                          {formatCurrency(eq.dailyRate)}/nap
                        </span>
                      </span>
                      {/* REUSE V2: státusz badge-ek */}
                      <span
                        className={cn(
                          'shrink-0 text-[11px] font-semibold',
                          !isAvailable
                            ? 'text-destructive'
                            : inCart
                              ? 'text-muted-foreground'
                              : 'text-[var(--accent,#0f766e)]'
                        )}
                        data-testid={
                          !isAvailable
                            ? `add-machine-result-unavailable-${eq.id}`
                            : inCart
                              ? `add-machine-result-incart-${eq.id}`
                              : `add-machine-result-add-${eq.id}`
                        }
                      >
                        {!isAvailable
                          ? t(STATUS_LABEL_KEYS[eq.status])
                          : inCart
                            ? t('rental.wizard.v2.search.alreadyAdded')
                            : t('rental.wizard.v2.detail.addMachineAddAction')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bal rail: bérelt gépek listája ────────────────────────────────────────

interface MachinesRailProps {
  cartItems: CartItem[];
  activeCartItemId: string | undefined;
  onSelectCartItem: (id: string) => void;
  onAddMachine: () => void;
}

function MachinesRail({
  cartItems,
  activeCartItemId,
  onSelectCartItem,
  onAddMachine,
}: MachinesRailProps) {
  const { t } = useTranslation();
  // BE-story #2: accCount badge — store-ból (proto steps2.jsx:48 accCount)
  // Subscribál az egész Map-re (shallow); a rail re-renderel ha bármely gépnél acc változik.
  const allCatalogQtys = useWizardStore(s => s.accessoryCatalogQtys);
  const getAccCount = (equipmentId: string): number => {
    const qtys = allCatalogQtys.get(equipmentId) ?? {};
    return Object.values(qtys).reduce((sum, e) => sum + e.qty, 0);
  };

  return (
    <aside
      className="w-full shrink-0 lg:w-[260px] space-y-2"
      data-testid="detail-editor-machines-rail"
    >
      <div className="flex items-center justify-between px-1">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('rental.wizard.v2.detail.railTitle')}
        </h3>
        <span className="text-[11px] text-muted-foreground" data-testid="detail-editor-rail-count">
          {t('rental.wizard.v2.detail.railCount', { n: cartItems.length })}
        </span>
      </div>

      {cartItems.length === 0 ? (
        <p
          className="text-xs text-muted-foreground italic px-1"
          data-testid="detail-editor-rail-empty"
        >
          {t('rental.wizard.v2.detail.railEmpty')}
        </p>
      ) : (
        <ul className="space-y-1" data-testid="detail-editor-rail-list">
          {cartItems.map((item, idx) => {
            const isActive = item.id === activeCartItemId;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onSelectCartItem(item.id)}
                  data-testid={`detail-editor-rail-item-${item.id}`}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'w-full text-left rounded-md border px-3 py-2 transition-colors',
                    isActive
                      ? 'border-[var(--accent,#0f766e)] bg-[var(--accent-soft,#e6f4f1)]'
                      : 'border-border hover:border-border-strong hover:bg-bg/60'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-medium',
                        isActive
                          ? 'bg-[var(--accent,#0f766e)] text-white'
                          : 'border border-[var(--border-secondary,#cbd5e1)] text-muted-foreground'
                      )}
                      aria-hidden="true"
                    >
                      {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div
                          className="truncate text-xs font-semibold"
                          data-testid={`detail-editor-rail-item-name-${item.id}`}
                        >
                          {item.equipment.name}
                        </div>
                        {/* BE-story #2: accCount badge — store-ból (proto steps2.jsx:48) */}
                        {(() => {
                          const accCount = getAccCount(item.equipment.id);
                          return accCount > 0 ? (
                            <span
                              className="inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--accent,#0f766e)] px-1.5 py-0.5 text-[10px] font-semibold text-white"
                              data-testid={`detail-editor-rail-item-acccount-${item.id}`}
                              data-acccount={accCount}
                            >
                              {accCount}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      {item.equipment.equipmentCode && (
                        <div
                          className="truncate font-mono text-[10px] text-muted-foreground"
                          data-testid={`detail-editor-rail-item-sku-${item.id}`}
                        >
                          {item.equipment.equipmentCode}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={onAddMachine}
        className="w-full rounded-md border border-dashed border-border py-2 text-xs text-muted-foreground hover:border-border-strong hover:text-fg transition-colors"
        data-testid="detail-editor-add-machine-btn"
      >
        {t('rental.wizard.v2.detail.addMachineButton')}
      </button>
    </aside>
  );
}

// ─── Közép: aktív gép detail shell ─────────────────────────────────────────

interface ActiveMachineDetailProps {
  cartItem: CartItem;
}

function ActiveMachineDetail({ cartItem }: ActiveMachineDetailProps) {
  const { t } = useTranslation();
  const { equipment } = cartItem;
  const categoryLabel = t(CATEGORY_LABEL_KEYS[equipment.category]);

  // days: a store pricing.durationDays-ből (RENTABLE lineTotal = dailyRate × qty × days).
  // Ha nincs pricing (időszak nincs beállítva) → 1 fallback (egynapi összeg látható).
  const days = useWizardStore(s => s.pricing?.durationDays ?? 1);

  // Package integration — reuse meglévő hook + PackageContentBreakdown
  const { packages, isLoading: isPackagesLoading } = usePackagesForEquipment(equipment.id);
  const selections = useWizardStore(s => s.getSelectionsForEquipment(equipment.id));
  const addPackageSelection = useWizardStore(s => s.addPackageSelection);
  const setSelectionItemQty = useWizardStore(s => s.setSelectionItemQty);
  const removePackageSelection = useWizardStore(s => s.removePackageSelection);

  const handleAddPackage = (pkg: Package) => {
    addPackageSelection({
      equipmentId: equipment.id,
      packageId: pkg.id,
      packageName: pkg.name,
      packageCode: pkg.packageCode,
    });
  };

  return (
    <section className="min-w-0 flex-1" data-testid="detail-editor-active-machine">
      {/* v3-paritás item #1 — folyamatos sűrű detail-panel: a korábbi külön
          bordered kártya-stack (header/csomag/katalógus külön lebegő kártya
          + space-y-4 rés) helyett EGY panel, a szekciók border-t-vel tagolva.
          Ez adja a proto „sűrű 3-oszlop" gestaltját (megszünteti a „messze van"
          érzést). A testid-ek + DOM-struktúra változatlan → proto-parity ZÖLD. */}
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        {/* Fejléc: gépnév + kód + napidíj */}
        <header className="px-4 py-3" data-testid="detail-editor-active-header">
          <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
            {categoryLabel}
          </div>
          <h2 className="text-base font-bold" data-testid="detail-editor-active-name">
            {equipment.name}
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {equipment.equipmentCode && (
              <span data-testid="detail-editor-active-sku">
                {t('rental.wizard.v2.detail.detailSku')}: {equipment.equipmentCode}
              </span>
            )}
            <span data-testid="detail-editor-active-daily-rate">
              {t('rental.wizard.v2.detail.detailDailyRate')}: {formatCurrency(equipment.dailyRate)}
            </span>
          </div>
        </header>

        {/* Csomag-választó + PackageContentBreakdown reuse */}
        <section
          className="border-t border-border px-4 py-3"
          data-testid="detail-editor-package-section"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('rental.wizard.v2.detail.detailPackageSection')}
          </h3>

          {isPackagesLoading ? (
            <div
              className="text-xs text-muted-foreground italic"
              data-testid="detail-editor-packages-loading"
            >
              {t('rental.wizard.v2.packagesLoading')}
            </div>
          ) : packages.length === 0 ? (
            <div
              className="text-xs text-muted-foreground italic"
              data-testid="detail-editor-packages-empty"
            >
              {t('rental.wizard.v2.packagesNoneAvailable')}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Csomag-chipek — hozzárendelt csomagok a useWizardStore selections-ből */}
              {selections.length > 0 && (
                <ul className="space-y-2" data-testid="detail-editor-selections-list">
                  {selections.map(sel => (
                    <li key={sel.selectionId} data-testid={`detail-editor-sel-${sel.selectionId}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium">{sel.packageName}</span>
                        <button
                          type="button"
                          onClick={() => removePackageSelection(sel.selectionId, 'user-action')}
                          className="text-[11px] text-muted-foreground hover:text-destructive transition-colors"
                          aria-label={t('rental.wizard.v2.packageRemoveAriaLabel', {
                            name: sel.packageName,
                          })}
                          data-testid={`detail-editor-sel-remove-${sel.selectionId}`}
                        >
                          ×
                        </button>
                      </div>
                      <PackageContentBreakdown
                        pkg={packages.find(p => p.id === sel.packageId)}
                        editable={{
                          selectionId: sel.selectionId,
                          ...(sel.qtyOverrides ? { qtyOverrides: sel.qtyOverrides } : {}),
                          onQtyChange: setSelectionItemQty,
                        }}
                        showLineTotal
                      />
                    </li>
                  ))}
                </ul>
              )}

              {/* Csomag-hozzáadás: package chip gombként listázva */}
              <div className="flex flex-wrap gap-1.5" data-testid="detail-editor-pkg-picker">
                {packages.map(pkg => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => handleAddPackage(pkg)}
                    className="rounded-full border border-border px-2.5 py-0.5 text-[11px] font-medium hover:border-[var(--accent,#0f766e)] hover:text-[var(--accent,#0f766e)] transition-colors"
                    data-testid={`detail-editor-pkg-add-${pkg.id}`}
                  >
                    + {pkg.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* slice-2: tartozék-katalógus */}
        <section
          className="border-t border-border px-4 py-3"
          data-testid="detail-editor-catalog-wrapper"
        >
          <AccessoryCatalogSection equipmentId={equipment.id} days={days} />
        </section>

        {/* slice-2: jelmagyarázat panel */}
        <div className="border-t border-border px-4 py-3">
          <AccessoryLegend />
        </div>
      </div>
    </section>
  );
}

// ─── Jobb: gépenkénti kalkuláció (slice-3) ──────────────────────────────────

/**
 * MachineCalcSide — jobb panel, gépenkénti kalkuláció.
 *
 * Pricing SSoT: `wizard-store.ts` `calculateCartPricing()` által számolt
 * `CartPricing` (pricing.items: CartPricingItem[], pricing.grandTotal,
 * pricing.vatAmount, pricing.depositTotal, pricing.durationDays).
 *
 * Per-gép bontás: `CartPricingItem.itemSubtotal` = gépsor nettó
 * (dailyRate × durationDays, tier-arányos). A csomaghoz tartozó
 * `rentAccNet`/`consignNet` per-gép bontás NEM áll rendelkezésre a
 * live store-ban (a packageSelections[] csak névleges linkeket tárol,
 * pricing nem aggregál csomagárat) → BE-illesztési igény, jelzett.
 *
 * Design-referencia: `rpi-berles/src/steps2.jsx` MachinesSide + pricing.js.
 */
function MachineCalcSide() {
  const { t } = useTranslation();
  const { pricing, startDate, endDate } = useWizardStore(
    useShallow(s => ({
      pricing: s.pricing,
      startDate: s.startDate,
      endDate: s.endDate,
    }))
  );

  // Dátum-formázó (hu-HU MM.DD rövid forma)
  const shortDate = (d: Date): string =>
    `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

  return (
    <aside
      className="hidden w-[220px] shrink-0 xl:flex xl:flex-col xl:gap-3"
      data-testid="detail-editor-calc-side"
    >
      {/* Időszak-panel */}
      <section
        className="rounded-md border border-border bg-bg px-3 py-2.5"
        data-testid="detail-editor-calc-period"
      >
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('rental.wizard.v2.detail.calcPeriodTitle')}
        </span>
        {pricing ? (
          <>
            <span
              className="block text-xl font-bold tabular-nums leading-tight"
              data-testid="detail-editor-calc-days"
              data-days={pricing.durationDays}
            >
              {t('rental.wizard.v2.detail.calcDays', { n: pricing.durationDays })}
            </span>
            {startDate && endDate && (
              <span
                className="mt-0.5 block font-mono text-[11px] text-muted-foreground tabular-nums"
                data-testid="detail-editor-calc-daterange"
              >
                {shortDate(startDate)} → {shortDate(endDate)}
              </span>
            )}
          </>
        ) : (
          <span
            className="text-xs italic text-muted-foreground"
            data-testid="detail-editor-calc-no-period"
          >
            —
          </span>
        )}
      </section>

      {/* Gépenkénti bontás panel */}
      <section
        className="rounded-md border border-border bg-bg px-3 py-2.5"
        data-testid="detail-editor-calc-per-machine"
      >
        <span className="mb-2 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('rental.wizard.v2.detail.calcPerMachineTitle')}
        </span>
        {pricing && pricing.items.length > 0 ? (
          <ul className="flex flex-col gap-3" data-testid="detail-editor-calc-machine-list">
            {pricing.items.map(it => (
              <li key={it.cartItemId} data-testid={`detail-editor-calc-machine-${it.cartItemId}`}>
                {/* Gépnév + gépsor nettó */}
                <div className="flex items-center justify-between gap-1">
                  <span
                    className="truncate text-[12px] font-semibold"
                    data-testid={`detail-editor-calc-machine-name-${it.cartItemId}`}
                  >
                    {it.equipmentName}
                  </span>
                  <span
                    className="shrink-0 tabular-nums text-[13px] font-bold"
                    data-testid={`detail-editor-calc-machine-net-${it.cartItemId}`}
                  >
                    {formatCurrency(it.itemSubtotal)}
                  </span>
                </div>
                {/* Alábontás: bérdíj × nap */}
                <div className="mt-0.5 flex items-center justify-between gap-1 text-[11px] text-muted-foreground">
                  <span data-testid={`detail-editor-calc-machine-rent-label-${it.cartItemId}`}>
                    {t('rental.wizard.v2.detail.calcMachineRent', {
                      rate: it.dailyRate,
                      days: it.durationDays,
                    })}
                  </span>
                  <span
                    className="shrink-0 tabular-nums"
                    data-testid={`detail-editor-calc-machine-rent-${it.cartItemId}`}
                  >
                    {formatCurrency(it.itemSubtotal)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <span
            className="text-xs italic text-muted-foreground"
            data-testid="detail-editor-calc-no-machines"
          >
            {t('rental.wizard.v2.detail.calcNoPricing')}
          </span>
        )}
      </section>

      {/* Becsült össz. (bruttó) total-card */}
      <div
        className="rounded-md bg-[var(--accent,#0f766e)] px-3 py-3 text-white"
        data-testid="detail-editor-calc-total-card"
      >
        <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-80">
          {t('rental.wizard.v2.detail.calcEstTotal')}
        </div>
        <div className="text-xl font-bold tabular-nums" data-testid="detail-editor-calc-gross">
          {pricing ? formatCurrency(pricing.grandTotal) : '—'}
        </div>
        {pricing && (
          <div
            className="mt-0.5 text-[11px] opacity-75"
            data-testid="detail-editor-calc-note"
            data-machines={pricing.items.length}
            data-days={pricing.durationDays}
          >
            {t('rental.wizard.v2.detail.calcNote', {
              m: pricing.items.length,
              d: pricing.durationDays,
            })}
          </div>
        )}
      </div>
    </aside>
  );
}

// ─── Fő export ───────────────────────────────────────────────────────────────

/**
 * EquipmentDetailEditorStep — `RENTAL_STEP2_DETAIL_EDITOR` flag-ON esetén
 * rendereli a StepWizardShell step-2 ágában az `EquipmentPackageStepV2` helyett.
 *
 * Flag-OFF esetén SOHA nem rendereli semmilyen szülő — ez a komponens csak
 * a detail-editor ágban él (regresszió-lock: flag-OFF → V2 marad).
 */
export function EquipmentDetailEditorStep() {
  const { t } = useTranslation();

  // Kosár-gépek a wizard-store-ból (useShallow: csak cart-változásra renderedül)
  const cart = useWizardStore(useShallow(s => s.cart));

  // Aktív gép — lokál useState (slice-1 váz; store-szerződés NEM módosul)
  // Alapértelmezett: az első kosárelemre mutat (ha van), különben undefined.
  const [activeCartItemId, setActiveCartItemId] = useState<string | undefined>(() => cart[0]?.id);

  // AddMachineDialog nyitott állapota
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Ha a kosár megváltozik (gép kivéve) és az aktív elem már nincs benne, reset.
  const activeCartItem = cart.find(item => item.id === activeCartItemId);

  // Ha az aktív elem eltűnt a kosárból, az első elemre állunk, különben undefined.
  const resolvedActiveItem = activeCartItem ?? (cart.length > 0 ? cart[0] : undefined);

  // Callback: add után az equipment.id-vel → a friss store state-ből keressük a cart-item.id-t.
  // (A cart closure stale lenne — a store szinkron, getState mindig friss.)
  const handleAfterAdd = (equipmentId: string) => {
    const freshCart = useWizardStore.getState().cart;
    const added = freshCart.find(ci => ci.equipment.id === equipmentId);
    if (added) setActiveCartItemId(added.id);
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row" data-testid="detail-editor-root">
      {/* AddMachineDialog — valódi gép-kereső + kosárba adás */}
      <AddMachineDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onAfterAdd={handleAfterAdd}
      />

      {/* Bal rail */}
      <MachinesRail
        cartItems={cart}
        activeCartItemId={resolvedActiveItem?.id}
        onSelectCartItem={setActiveCartItemId}
        onAddMachine={() => setAddDialogOpen(true)}
      />

      {/* Közép aktív-gép-detail shell */}
      {resolvedActiveItem ? (
        <ActiveMachineDetail cartItem={resolvedActiveItem} />
      ) : (
        <section
          className="min-w-0 flex-1 flex items-center justify-center rounded-md border border-dashed border-border bg-bg/40 py-10"
          data-testid="detail-editor-center-empty"
        >
          <p className="text-sm text-muted-foreground">
            {t('rental.wizard.v2.detail.activeEmpty')}
          </p>
        </section>
      )}

      {/* Jobb kalkuláció panel (slice-3) */}
      <MachineCalcSide />
    </div>
  );
}
